import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';
import { MOBILE_PATH, shouldRedirectToMobile, currentRedirectInputs } from '~/lib/mobile-redirect';

/** The theme actually painted — always concrete, never "system". */
export type Theme = 'light' | 'dark';

/**
 * What the user (or the surface's own default) has chosen. `'system'` means
 * "follow the OS setting live" — the resolved `Theme` tracks
 * `prefers-color-scheme` for as long as this stays the preference.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

interface ThemeState {
  preference: ThemePreference;
  theme: Theme;
  setPreference: (preference: ThemePreference) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'eddy:theme';
/**
 * FRONT-1342 migration marker. From mid-2026 (commit `48517f02`) until this
 * feature, the store's only default was a hardcoded `'light'`, persisted on
 * EVERY boot — so a browser that loaded Eddy during that window already
 * carries a stored `'light'` the user never actually chose, confirmed
 * directly against this project's own captured local session.
 *
 * That claim does NOT hold for the whole of history, though: before
 * `48517f02` (commit `add77702` onward), the default instead followed the OS
 * (`prefers-color-scheme`) and was ALSO persisted unconditionally — so a
 * browser that first loaded Eddy in THAT earlier window, with a dark OS,
 * carries an auto-written `'dark'`, not `'light'`. `resolveStoredPreference`
 * deliberately does not try to unwind that: an auto-written `'dark'` is
 * indistinguishable from a deliberately-picked one, and trusting it means a
 * dark-preferring browser opens dark — this feature's own goal — so leaving
 * it alone costs nothing. Only `'light'` is targeted because only `'light'`
 * failed the goal (it forced a light phone open for a dark-OS user).
 *
 * Presence of this key (any value) means the one-time cleanup in
 * `migrateStaleLightDefault` has already run in this browser.
 */
export const MIGRATION_MARKER_KEY = 'eddy:theme-default-migrated';

/**
 * Resolve a preference to a concrete theme. Pure — no DOM, no storage — so
 * it's unit-testable on its own and reused by the store, the live
 * OS-following listener, and (as a hand-copied twin, drift-guarded by
 * `theme-boot.test.ts`) the pre-paint script in `index.html`.
 */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): Theme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

/**
 * Parse a raw `localStorage` value into a preference. Accepts the legacy
 * `'light'`/`'dark'` values (written before `'system'` existed) and the new
 * `'system'` literal; anything else (unset, corrupt, a future value this
 * build doesn't know) is "no stored preference" rather than a guess.
 */
export function parseStoredPreference(raw: string | null): ThemePreference | null {
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return null;
}

/**
 * Applies the FRONT-1342 migration rule on top of `parseStoredPreference`: a
 * stored `'light'` with no migration marker is treated as absent — resolving
 * as if nothing were stored at all, falling through to `defaultPreference` —
 * because the old code auto-wrote `'light'` on every boot (see
 * `MIGRATION_MARKER_KEY`), so most stored `'light'`s were never a deliberate
 * pick. `'dark'` and `'system'` are returned untouched regardless of the
 * marker.
 *
 * Known, accepted trade-off: a stored `'light'` COULD have been a genuine
 * choice under the old build too (toggle to dark, then deliberately back to
 * light) — that case is indistinguishable from the auto-write and this rule
 * discards it anyway. Unlocking the phone's dark default for every existing
 * user was judged worth losing that rarer, silent case; see
 * `MIGRATION_MARKER_KEY`'s doc comment for the mirror-image case on the
 * `'dark'` side, which is deliberately left untouched instead.
 *
 * Pure and read-only — this is the ONE resolution rule both the store
 * (`getInitialPreference`) and the pre-paint script in `index.html` (a
 * necessarily hand-copied twin, drift-guarded by `theme-boot.test.ts`) apply.
 * The physical cleanup (deleting the stale key, setting the marker) is a
 * SEPARATE, store-only side effect — see `migrateStaleLightDefault` — that
 * this function has no opinion about and does not depend on having run.
 */
export function resolveStoredPreference(
  raw: string | null,
  migrated: boolean,
): ThemePreference | null {
  const parsed = parseStoredPreference(raw);
  if (parsed === 'light' && !migrated) return null;
  return parsed;
}

function isPhoneRoute(pathname: string): boolean {
  return pathname === '/m' || pathname.startsWith(MOBILE_PATH);
}

/**
 * The preference a user who has never touched the theme control gets —
 * BEFORE anything is written to storage (FRONT-1342): `'system'` exactly when this browser IS on, or
 * WOULD BE SENT to, the phone surface — never merely "looks phone-shaped".
 * A phone-shaped browser sitting on a desktop-only deep link (`/work/abc123`,
 * a page reached via "Use the desktop version") still defaults to light,
 * because it would never actually be redirected there.
 * `wouldRedirectToMobile` is `shouldRedirectToMobile`'s answer for the SAME
 * browser signals — the caller computes it (see `getInitialPreference`) so
 * this stays a pure, explicit-input function.
 */
export function defaultPreference(pathname: string, wouldRedirectToMobile: boolean): ThemePreference {
  return isPhoneRoute(pathname) || wouldRedirectToMobile ? 'system' : 'light';
}

/** The default for THIS browser. */
function currentDefaultPreference(): ThemePreference {
  const inputs = currentRedirectInputs();
  return defaultPreference(inputs.pathname, shouldRedirectToMobile(inputs));
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Paint the resolved theme. Does NOT persist — see `getInitialPreference`. */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.setAttribute('data-theme', theme);
}

/**
 * Persist the preference. Wrapped: a browser that throws on storage access
 * (blocked cookies in an embedded iframe, hardened Firefox, Safari private
 * mode) must not throw HERE either — the in-memory `preference`/`theme`
 * state (set right after this call in every caller below) and the DOM paint
 * from `applyTheme` both still need to go through; only the persistence
 * itself is allowed to silently fail.
 *
 * Also ensures the migration marker (FIX H, FRONT-1342 review round 2): if
 * the marker's own `setItem` ever failed once (e.g. quota) while the theme
 * key stayed writable, an explicit pick would be written with no marker
 * present — and `migrateStaleLightDefault` treats ANY marker-less stored
 * `'light'` as the stale pre-migration auto-write, deleting a real user
 * choice on the very next boot. `setPreference`/`setTheme`/`toggleTheme` all
 * persist through this one function, so retrying the marker here on every
 * explicit persist (not just the original one-time migration) gives the
 * marker many chances to catch up before the user ever reloads.
 */
function persistPreference(preference: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
    ensureMigrationMarker();
  } catch {
    // Storage inaccessible — see doc comment above.
  }
}

function isMigrated(): boolean {
  try {
    return localStorage.getItem(MIGRATION_MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

/** Sets the migration marker if it isn't already set. Idempotent, and safe
 *  to call unconditionally — see `persistPreference`'s doc comment for why
 *  this needs to be retried on every explicit persist, not just once. */
function ensureMigrationMarker(): void {
  if (localStorage.getItem(MIGRATION_MARKER_KEY) === null) {
    localStorage.setItem(MIGRATION_MARKER_KEY, '1');
  }
}

/**
 * One-time physical cleanup for the pre-migration stale write (FRONT-1342):
 * if the marker isn't set yet, drop a stored `'light'` (almost always the old
 * code's every-boot auto-write, not a deliberate pick — see the trade-off
 * noted on `resolveStoredPreference`) and set the marker either way, so this
 * can never run twice. A stored `'dark'`/`'system'` is left untouched. This
 * is pure hygiene — `resolveStoredPreference` is what actually decides
 * correctness (see `getInitialPreference`), so a private-mode throw here
 * degrades gracefully rather than corrupting the resolved theme; every
 * access is wrapped for exactly that reason.
 */
function migrateStaleLightDefault(): void {
  try {
    if (localStorage.getItem(MIGRATION_MARKER_KEY) !== null) return; // already migrated
    if (localStorage.getItem(STORAGE_KEY) === 'light') {
      localStorage.removeItem(STORAGE_KEY);
    }
    localStorage.setItem(MIGRATION_MARKER_KEY, '1');
  } catch {
    // Storage inaccessible (private mode) — nothing to clean up.
  }
}

/**
 * The starting preference for a fresh store. CRITICAL: a preference that
 * came from `defaultPreference` (nothing was ever explicitly chosen) is
 * NEVER written to storage here — only `setPreference`/`setTheme`/
 * `toggleTheme` persist, so a user who never picked keeps following
 * whatever the default is, including if it changes again.
 *
 * The read is wrapped: a browser that throws on storage access must not
 * throw HERE, inside the store constructor — the pre-paint script in
 * `index.html` already painted correctly before this ever runs, and an
 * uncaught throw here would blank the app on top of that correct paint
 * rather than merely lose the (already-lost) stored preference.
 */
function getInitialPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    raw = null; // Storage inaccessible — see doc comment above.
  }
  const stored = resolveStoredPreference(raw, isMigrated());
  return stored ?? currentDefaultPreference();
}

/**
 * Wires the three mutating actions against a store's `set`. Split out of
 * `createThemeStore` so store construction (initial state, persistence,
 * listener wiring) reads as orchestration rather than being interleaved with
 * the action bodies themselves — behavior is unchanged, this is the exact
 * same three closures that used to live inline in the initializer.
 */
function createThemeActions(
  set: StoreApi<ThemeState>['setState'],
): Pick<ThemeState, 'setPreference' | 'setTheme' | 'toggleTheme'> {
  return {
    setPreference: (preference) => {
      const theme = resolveTheme(preference, systemPrefersDark());
      applyTheme(theme);
      persistPreference(preference);
      set({ preference, theme });
    },
    // An explicit light/dark pick — same contract as before 'system' existed:
    // the desktop Sun/Moon toggle keeps working exactly as it did.
    setTheme: (theme) => {
      applyTheme(theme);
      persistPreference(theme);
      set({ preference: theme, theme });
    },
    toggleTheme: () =>
      set((state) => {
        const next: Theme = state.theme === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        persistPreference(next);
        return { preference: next, theme: next };
      }),
  };
}

/**
 * Live OS following: while the preference is 'system', a change to the OS
 * setting re-resolves and re-paints immediately, without waiting for a
 * reload. Only the resolved `theme` changes here — the preference itself
 * is unchanged and this never persists anything.
 *
 * Defensive on purpose: `MediaQueryList.addEventListener` doesn't exist on
 * older Safari (it only ever had the deprecated `addListener` callback
 * form), and this runs inside store construction — every mounting
 * component calls through `createThemeStore`, so an uncaught throw here
 * would blank the app before anything renders. The pre-paint script in
 * index.html was already hardened against exactly this class of failure;
 * this listener gets the same treatment. Losing live OS-following on an
 * ancient browser is a much smaller cost than a blank screen.
 *
 * Split out of `createThemeStore` as its own step (rather than inlined
 * after the store is built) because the compatibility fallback and its
 * try/catch are the part least related to constructing the store itself.
 */
function installSystemThemeListener(store: StoreApi<ThemeState>): void {
  if (typeof window === 'undefined') return;
  try {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystemChange = (event: { matches: boolean }) => {
      if (store.getState().preference !== 'system') return;
      const next = resolveTheme('system', event.matches);
      applyTheme(next);
      store.setState({ theme: next });
    };
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onSystemChange);
    } else {
      const legacy = media as unknown as {
        addListener?: (listener: (event: { matches: boolean }) => void) => void;
      };
      legacy.addListener?.(onSystemChange);
    }
  } catch {
    // See doc comment above — never let a hostile/incomplete matchMedia
    // implementation prevent the store (and therefore the app) from
    // finishing construction.
  }
}

export function createThemeStore(): StoreApi<ThemeState> {
  // Runs before anything below reads storage, but correctness never depends
  // on this ordering — resolveStoredPreference is self-contained (see its
  // doc comment) and would resolve identically either way.
  if (typeof window !== 'undefined') migrateStaleLightDefault();

  const initialPreference = getInitialPreference();
  const initialTheme = resolveTheme(initialPreference, systemPrefersDark());
  applyTheme(initialTheme);

  const store = createStore<ThemeState>('theme-store', (set) => ({
    preference: initialPreference,
    theme: initialTheme,
    ...createThemeActions(set),
  }));

  installSystemThemeListener(store);

  return store;
}

let store: StoreApi<ThemeState> | undefined;

function getThemeStore() {
  if (!store) store = createThemeStore();
  return store;
}

export function useTheme() {
  const preference = useStore(getThemeStore(), (s) => s.preference);
  const theme = useStore(getThemeStore(), (s) => s.theme);
  const setPreference = useStore(getThemeStore(), (s) => s.setPreference);
  const setTheme = useStore(getThemeStore(), (s) => s.setTheme);
  const toggleTheme = useStore(getThemeStore(), (s) => s.toggleTheme);
  return { preference, theme, setPreference, setTheme, toggleTheme };
}
